import {useCallback, useEffect, useMemo, useState} from "react";
import {
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    IconButton, Typography, CircularProgress, Box, useTheme, TablePagination,
    TableSortLabel
} from "@mui/material";
import {KeyboardArrowDown, KeyboardArrowRight} from "@mui/icons-material";
import {apiGet} from "../utils";
import {buildV2QueryParams} from "../utils/filters";
import {formatCurrency, formatHierarchyNames, formatNumber, formatPercent, formatDate} from "../utils/formatters";
import {SegmentChip} from "./SegmentChip";

const CUSTOMER_CHURN_CALC_COLUMN_LABELS = {
    net_revenue: 'Net Revenue (12m)',
    variable_margin: 'Variable Margin (12m)',
    variable_margin_percent: 'VM %',
    avg_order_value: 'Avg Order Value',
    number_of_orders: 'Number of Orders',
};

const LEADING_CALC_TYPES = ['number_of_orders'];
const TRAILING_CALC_ORDER = [
    'net_revenue',
    'variable_margin',
    'variable_margin_percent',
    'avg_order_value',
];

const orderCalcTypes = (calcTypes) => {
    const set = new Set(calcTypes);
    const leading = LEADING_CALC_TYPES.filter((type) => set.has(type));
    const trailing = TRAILING_CALC_ORDER.filter((type) => set.has(type));
    const rest = calcTypes.filter(
        (type) => !LEADING_CALC_TYPES.includes(type) && !TRAILING_CALC_ORDER.includes(type)
    );
    return {leading, trailing: [...trailing, ...rest]};
};

const HIERARCHY_CELL_SX = {
    minWidth: 160,
    maxWidth: 220,
    whiteSpace: 'normal',
    wordBreak: 'break-word'
};

const formatMetricValue = (value, valueType) => {
    if (value === null || value === undefined) {
        return '—';
    }
    if (valueType === 'percent') {
        return formatPercent(value) ?? '0.0%';
    }
    if (valueType === 'count') {
        return formatNumber(value) ?? '0';
    }
    return formatCurrency(value) ?? '$0';
};

const getSortValue = (row, sortKey, calcTypes) => {
    if (sortKey === 'name') return row.name || '';
    if (sortKey === 'hierarchy_names') return formatHierarchyNames(row.hierarchy_names) || '';
    if (sortKey === 'number') return row.number ?? '';
    if (sortKey === 'segment') return row.segment || '';
    if (sortKey === 'date_last_order') return row.date_last_order || '';
    if (sortKey === 'days_since_last_order') return row.days_since_last_order ?? 0;
    if (sortKey === 'churn_probability_pct') return row.churn_probability_pct ?? 0;
    if (sortKey === 'risk_driver_1') return row.risk_driver_1 || '';
    if (sortKey === 'recommended_action') return row.recommended_action || '';
    if (calcTypes.includes(sortKey)) {
        const value = row[sortKey];
        return value?.current ?? value ?? 0;
    }
    return '';
};

const sortRows = (rows, sortKey, sortDir, calcTypes) => {
    if (!sortKey) return rows;
    const direction = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const aVal = getSortValue(a, sortKey, calcTypes);
        const bVal = getSortValue(b, sortKey, calcTypes);
        if (typeof aVal === 'string' || typeof bVal === 'string') {
            return String(aVal).localeCompare(String(bVal)) * direction;
        }
        if (aVal === bVal) {
            return String(a.name || '').localeCompare(String(b.name || '')) * direction;
        }
        return (aVal - bVal) * direction;
    });
};

const MetricCells = ({row, calcTypes, calcConfigs}) => (
    <>
        {calcTypes.map((calcType) => (
            <TableCell key={calcType} align="right">
                {formatMetricValue(
                    row[calcType]?.current ?? row[calcType],
                    calcConfigs[calcType]?.valueType
                )}
            </TableCell>
        ))}
    </>
);

const OrderActivityCells = ({row}) => (
    <>
        <TableCell align="right">{formatDate(row.date_last_order)}</TableCell>
        <TableCell align="right">{row.days_since_last_order ?? '—'}</TableCell>
    </>
);

const AiCells = ({row}) => (
    <>
        <TableCell align="right">
            {Number.isFinite(row?.churn_probability_pct) ? `${row.churn_probability_pct.toFixed(1)}%` : '—'}
        </TableCell>
        <TableCell align="left">
            <Typography variant="body2">{row?.risk_driver_1 || '—'}</Typography>
        </TableCell>
        <TableCell align="left">
            <Typography variant="body2">{row?.recommended_action || '—'}</Typography>
        </TableCell>
    </>
);

const ProductRow = ({product, leadingCalcTypes, trailingCalcTypes, calcConfigs}) => (
    <TableRow style={{background: '#FAFAFA'}}>
        <TableCell style={{paddingLeft: 56}}>
            <Typography variant="body2">{product.name}</Typography>
        </TableCell>
        <TableCell sx={HIERARCHY_CELL_SX}>
            <Typography variant="body2" color="textSecondary">
                {formatHierarchyNames(product.hierarchy_names) || '—'}
            </Typography>
        </TableCell>
        <TableCell>
            <Typography variant="body2">{product.number || '—'}</Typography>
        </TableCell>
        <TableCell/>
        <OrderActivityCells row={product}/>
        <MetricCells row={product} calcTypes={leadingCalcTypes} calcConfigs={calcConfigs}/>
        <MetricCells row={product} calcTypes={trailingCalcTypes} calcConfigs={calcConfigs}/>
        <TableCell colSpan={4}/>
    </TableRow>
);

const CustomerRow = ({
    row,
    leadingCalcTypes,
    trailingCalcTypes,
    calcConfigs,
    filters,
    onExpandProducts,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [products, setProducts] = useState(null);
    const [loading, setLoading] = useState(false);

    const toggle = () => {
        const next = !expanded;
        setExpanded(next);
        if (next && products === null) {
            setLoading(true);
            onExpandProducts(row.id, filters, (result) => {
                setProducts(result?.breakdown || []);
                setLoading(false);
            });
        }
    };

    return (
        <>
            <TableRow hover onClick={toggle} style={{cursor: 'pointer'}}>
                <TableCell>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <IconButton size="small">
                            {loading
                                ? <CircularProgress size={14}/>
                                : expanded
                                    ? <KeyboardArrowDown fontSize="small"/>
                                    : <KeyboardArrowRight fontSize="small"/>}
                        </IconButton>
                        <Typography variant="body2" style={{fontWeight: 600}}>{row.name}</Typography>
                    </Box>
                </TableCell>
                <TableCell sx={HIERARCHY_CELL_SX}>
                    <Typography variant="body2" color="textSecondary">
                        {formatHierarchyNames(row.hierarchy_names) || '—'}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{row.number || '—'}</Typography>
                </TableCell>
                <TableCell><SegmentChip segment={row.segment}/></TableCell>
                <OrderActivityCells row={row}/>
                <MetricCells row={row} calcTypes={leadingCalcTypes} calcConfigs={calcConfigs}/>
                <MetricCells row={row} calcTypes={trailingCalcTypes} calcConfigs={calcConfigs}/>
                <AiCells row={row}/>
            </TableRow>
            {expanded && !loading && products && products.map((product) => (
                <ProductRow
                    key={product.id}
                    product={product}
                    leadingCalcTypes={leadingCalcTypes}
                    trailingCalcTypes={trailingCalcTypes}
                    calcConfigs={calcConfigs}
                />
            ))}
        </>
    );
};

export const CustomerHealthTable = ({breakdown = [], calcConfigs = {}, filters}) => {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    const calcTypes = useMemo(() => Object.keys(calcConfigs), [calcConfigs]);
    const {leading: leadingCalcTypes, trailing: trailingCalcTypes} = useMemo(
        () => orderCalcTypes(calcTypes),
        [calcTypes]
    );
    const orderedCalcTypes = useMemo(
        () => [...leadingCalcTypes, ...trailingCalcTypes],
        [leadingCalcTypes, trailingCalcTypes]
    );
    const extraColumns = ['date_last_order', 'days_since_last_order', 'churn_probability_pct', 'risk_driver_1', 'recommended_action'];
    const columnCount = 4 + orderedCalcTypes.length + extraColumns.length;

    useEffect(() => {
        setPage(0);
    }, [breakdown]);

    const sortedRows = useMemo(
        () => sortRows(breakdown, sortKey, sortDir, orderedCalcTypes),
        [breakdown, sortKey, sortDir, orderedCalcTypes]
    );

    const paginatedRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    );

    const fetchProducts = useCallback((accountId, filterValues, cb) => {
        const query = buildV2QueryParams({
            ...filterValues,
            accountId,
        });
        apiGet(`v2/customer-churn/products${query}`, (resp) => {
            cb(resp.success ? resp.data : {breakdown: []});
        });
    }, []);

    const handleSort = (field) => {
        if (sortKey === field) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(field);
            setSortDir('asc');
        }
        setPage(0);
    };

    const renderSortLabel = (label, field) => (
        <TableSortLabel
            active={sortKey === field}
            direction={sortKey === field ? sortDir : 'asc'}
            onClick={() => handleSort(field)}
        >
            {label}
        </TableSortLabel>
    );

    return (
        <Paper style={{...theme.palette.contentPanel, padding: 0}}>
            <TableContainer>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        '& .MuiTableCell-head.MuiTableCell-stickyHeader': {
                            zIndex: 1,
                            backgroundColor: '#F5F7FB'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Name', 'name')}</TableCell>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Hierarchy', 'hierarchy_names')}</TableCell>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Number', 'number')}</TableCell>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Segment', 'segment')}</TableCell>
                            <TableCell align="right" style={{fontWeight: 600}}>{renderSortLabel('Date Last Order', 'date_last_order')}</TableCell>
                            <TableCell align="right" style={{fontWeight: 600}}>{renderSortLabel('Days Since Last Order', 'days_since_last_order')}</TableCell>
                            {leadingCalcTypes.map((calcType) => (
                                <TableCell key={calcType} align="right" style={{fontWeight: 600}}>
                                    {renderSortLabel(
                                        CUSTOMER_CHURN_CALC_COLUMN_LABELS[calcType] || calcConfigs[calcType]?.name || calcType,
                                        calcType
                                    )}
                                </TableCell>
                            ))}
                            {trailingCalcTypes.map((calcType) => (
                                <TableCell key={calcType} align="right" style={{fontWeight: 600}}>
                                    {renderSortLabel(
                                        CUSTOMER_CHURN_CALC_COLUMN_LABELS[calcType] || calcConfigs[calcType]?.name || calcType,
                                        calcType
                                    )}
                                </TableCell>
                            ))}
                            <TableCell align="right" style={{fontWeight: 600}}>{renderSortLabel('Churn Prob. %', 'churn_probability_pct')}</TableCell>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Risk Driver', 'risk_driver_1')}</TableCell>
                            <TableCell style={{fontWeight: 600}}>{renderSortLabel('Recommended Action', 'recommended_action')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {breakdown.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columnCount} style={{textAlign: 'center', padding: 30}}>
                                    <Typography color="textSecondary">No customers match the current filters</Typography>
                                </TableCell>
                            </TableRow>
                        ) : paginatedRows.map((row) => (
                            <CustomerRow
                                key={row.id}
                                row={row}
                                leadingCalcTypes={leadingCalcTypes}
                                trailingCalcTypes={trailingCalcTypes}
                                calcConfigs={calcConfigs}
                                filters={filters}
                                onExpandProducts={fetchProducts}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {breakdown.length > 0 && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={breakdown.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Paper>
    );
};
